package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Immutable
import androidx.compose.ui.graphics.Color

/**
 * Raw brand palette — "Directory & Community System" (see docs/DESIGN_SYSTEM.md).
 * Do NOT use these directly in feature code. Use [AppColors] via `AppTheme.colors`.
 */
internal object Palette {
    val Teal = Color(0xFF006D77)          // primary
    val TealDark = Color(0xFF00535B)      // primary pressed / strong
    val TealContainer = Color(0xFF9BECF7) // light teal fills
    val CategoryChip = Color(0xFFE0F2F1)  // light teal chip bg
    val Saffron = Color(0xFFE9C46A)       // secondary / accent (stars, premium, CTA highlight) + logo gold
    val BrandInk = Color(0xFF052F2E)      // deep teal behind the logo mark (launcher/splash/hero tiles) — matches the 2026-07-18 logo art ink
    val SecondaryContainer = Color(0xFFFFD87C) // gold container (verified/network pill)
    val OnSecondaryContainer = Color(0xFF795D08) // text on gold container
    val Charcoal = Color(0xFF264653)      // tertiary / dark text accents, sponsored badge bg
    val Background = Color(0xFFF8F9FA)     // app background (off-white)
    val Surface = Color(0xFFFFFFFF)        // cards, sheets, inputs (surface-container-lowest)
    val SurfaceContainer = Color(0xFFEDEEEF) // neutral chips/circles (icon buttons)
    val SurfaceContainerHigh = Color(0xFFE7E8E9)
    val OnSurface = Color(0xFF191C1D)      // primary text
    val OnSurfaceVariant = Color(0xFF3E494A) // secondary text / metadata
    val Outline = Color(0xFF6F797A)        // strong borders
    val OutlineVariant = Color(0xFFBEC8CA) // hairline borders (Directory & Community System token)
    val Error = Color(0xFFBA1A1A)
    val ErrorContainer = Color(0xFFFFDAD6) // soft red fill (error banners/snackbars)
    val OnErrorContainer = Color(0xFF410002) // text/icon on error container
    // Open/active. Darkened from the flag green #239F40 (3.44:1 on white — fails WCAG AA for the
    // small "Open now" labels) to the nearest same-hue value that clears 4.5:1 on surface AND background.
    val Success = Color(0xFF1B8038)
    val Alert = Color(0xFFDA0000)          // closed/alert (flag red)
    // Official brand colors of the share-sheet networks (theme-invariant; badges on ShareSheet).
    val Telegram = Color(0xFF26A5E4)
    val WhatsApp = Color(0xFF25D366)
    val Instagram = Color(0xFFE4405F)
    val Facebook = Color(0xFF1877F2)
    val White = Color(0xFFFFFFFF)
    val Black = Color(0xFF000000)
}

/**
 * Semantic color tokens consumed across the app. Access via `AppTheme.colors`.
 */
@Immutable
data class AppColors(
    val primary: Color,
    val onPrimary: Color,
    val primaryContainer: Color,
    val onPrimaryContainer: Color,
    val secondary: Color,        // saffron accent
    val onSecondary: Color,
    val secondaryContainer: Color, // gold container (pills/badges)
    val onSecondaryContainer: Color,
    val tertiary: Color,         // charcoal
    val onTertiary: Color,
    val background: Color,
    val onBackground: Color,
    val surface: Color,
    val surfaceContainer: Color,
    val surfaceContainerHigh: Color,
    val onSurface: Color,
    val onSurfaceVariant: Color,
    val outline: Color,
    val outlineVariant: Color,
    val categoryChip: Color,
    val error: Color,
    val onError: Color,
    val errorContainer: Color,
    val onErrorContainer: Color,
    val success: Color,          // open / active
    val alert: Color,            // closed / alert
    /**
     * Full-bleed brand header band (top nav, Home's search header, the Gems readers). Deliberately a
     * token of its own rather than `primary`: `primary` is a FOREGROUND role and inverts to light
     * aqua in dark, which turned the header into pale cyan with unreadable white content. This one
     * stays a deep teal in BOTH themes and is always paired with [onBrandBand].
     */
    val brandBand: Color,
    val onBrandBand: Color,
    val brandInk: Color,         // deep teal behind the logo mark (splash/hero/brand tiles)
    val brandGold: Color,        // the logo mark's gold (= saffron accent)
    // Share-network brand badges (official colors; theme-invariant like the scrim pair).
    val brandTelegram: Color,
    val brandWhatsApp: Color,
    val brandInstagram: Color,
    val brandFacebook: Color,
    val scrim: Color,            // immersive full-screen media backdrop (photo viewer)
    val onScrim: Color,          // icons/text over the scrim
    val isDark: Boolean,
)

// Token names mirror the Directory & Community System (Stitch M3) 1:1, so Stitch classes map directly:
//   bg-primary (#00535b) -> primary · bg-primary-container (#006d77) -> primaryContainer
//   text-on-primary-container (#9becf7) -> onPrimaryContainer
val lightAppColors: AppColors = AppColors(
    primary = Palette.TealDark,          // #00535b — text-primary / brand text & active
    onPrimary = Palette.White,
    primaryContainer = Palette.Teal,     // #006d77 — filled buttons, logo tiles, brand fill
    onPrimaryContainer = Palette.TealContainer, // #9becf7
    secondary = Palette.Saffron,
    onSecondary = Palette.Charcoal,
    secondaryContainer = Palette.SecondaryContainer,
    onSecondaryContainer = Palette.OnSecondaryContainer,
    tertiary = Palette.Charcoal,
    onTertiary = Palette.White,
    background = Palette.Background,
    onBackground = Palette.OnSurface,
    surface = Palette.Surface,
    surfaceContainer = Palette.SurfaceContainer,
    surfaceContainerHigh = Palette.SurfaceContainerHigh,
    onSurface = Palette.OnSurface,
    onSurfaceVariant = Palette.OnSurfaceVariant,
    outline = Palette.Outline,
    outlineVariant = Palette.OutlineVariant,
    categoryChip = Palette.CategoryChip,
    error = Palette.Error,
    onError = Palette.White,
    errorContainer = Palette.ErrorContainer,
    onErrorContainer = Palette.OnErrorContainer,
    success = Palette.Success,
    alert = Palette.Alert,
    brandBand = Palette.TealDark,   // #00535b — white content sits at 8.79:1
    onBrandBand = Palette.White,
    brandInk = Palette.BrandInk,
    brandGold = Palette.Saffron,
    brandTelegram = Palette.Telegram,
    brandWhatsApp = Palette.WhatsApp,
    brandInstagram = Palette.Instagram,
    brandFacebook = Palette.Facebook,
    // Photos read best on pure black in BOTH themes (industry standard for media viewers),
    // so the scrim pair intentionally does not invert in darkAppColors.
    scrim = Palette.Black,
    onScrim = Palette.White,
    isDark = false,
)

// Full dark variant (M3 inversion of the Directory & Community System palette): containers go dark,
// their "on" colors go light; accents (saffron, teal) brighten a step so they carry on dark grounds.
val darkAppColors: AppColors = lightAppColors.copy(
    primary = Palette.TealContainer,            // #9becf7 — brand text/active on dark
    // onPrimary stays white: this app always pairs it with the teal primaryContainer fill
    // (buttons, chips, teal headers) — never with the light-aqua dark `primary`.
    onPrimary = Palette.White,
    primaryContainer = Palette.Teal,            // filled buttons stay brand teal (white content)
    onPrimaryContainer = Palette.TealContainer,
    secondary = Palette.Saffron,                // stars/accents read well on dark as-is
    onSecondary = Palette.Charcoal,
    secondaryContainer = Color(0xFF5A4400),     // dark gold container…
    onSecondaryContainer = Color(0xFFFFDF96),   // …with light gold content (was dark-on-light)
    tertiary = Color(0xFF3A5866),               // charcoal lifts a step so badges don't vanish
    onTertiary = Color(0xFFE7F3F8),
    background = Color(0xFF0F1416),
    onBackground = Color(0xFFE1E3E4),
    surface = Color(0xFF1A1F21),
    surfaceContainer = Color(0xFF232A2C),       // was light #EDEEEF — glared in dark (chips, icon circles)
    surfaceContainerHigh = Color(0xFF2B3335),   // was light #E7E8E9 — glared in dark (neutral tiles)
    onSurface = Color(0xFFE1E3E4),
    onSurfaceVariant = Color(0xFFBEC8CA),
    // Lifted from #89938F (4.08:1 on surfaceContainerHigh — under AA for the small metadata that
    // uses it) to clear 4.5:1 against EVERY dark ground.
    outline = Color(0xFF939D99),
    // Was #2E3537 — DARKER than surfaceContainerHigh (#2B3335), so card hairlines were invisible
    // (1.03:1) on elevated surfaces and cards lost their edges. In M3 dark, outlineVariant must sit
    // lighter than the surfaces it separates. #454F52 clears 1.5:1 on all four grounds
    // (bg 2.20 / surface 1.98 / container 1.73 / containerHigh 1.53).
    outlineVariant = Color(0xFF454F52),
    categoryChip = Color(0xFF103A3C),
    // Header band goes DARKER in dark mode (not lighter): deep teal that still reads as brand, stays
    // distinguishable from the dark background (1.47:1) and carries white content at 12.65:1 and the
    // saffron active-accent at 7.57:1.
    brandBand = Color(0xFF06393D),
    onBrandBand = Palette.White,
    error = Color(0xFFFFB4AB),                  // M3 dark error pair
    onError = Color(0xFF690005),
    errorContainer = Color(0xFF93000A),
    onErrorContainer = Color(0xFFFFDAD6),
    // Was #FF5449 (4.06:1 on surfaceContainerHigh); brightened to clear AA everywhere, since
    // "Closed now" renders as small text on neutral tiles.
    alert = Color(0xFFFF6B61),
    success = Color(0xFF4CC96A),
    isDark = true,
)
